import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUpdatePastPaper } from '@/api/useAdminPastPapers';

interface PastPaperReadingInsertEditorProps {
  pastPaperId: string;
  currentPages: number[] | null;
  field?: 'reading_insert_page_numbers' | 'reading_insert_page_numbers_2';
}

interface FormValues {
  pages: string;
}

const PastPaperReadingInsertEditor: FC<PastPaperReadingInsertEditorProps> = ({
  pastPaperId,
  currentPages,
  field = 'reading_insert_page_numbers',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: updatePastPaper, isPending } = useUpdatePastPaper();

  const displayValue = currentPages && currentPages.length > 0 ? currentPages.join(', ') : '--';

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      pages: currentPages ? currentPages.join(', ') : '',
    },
  });

  const handleStartEdit = () => {
    reset({ pages: currentPages ? currentPages.join(', ') : '' });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = (data: FormValues) => {
    const trimmed = data.pages.trim();
    let pages: number[] | null = null;

    if (trimmed) {
      const parsed = trimmed
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0);

      if (parsed.length > 0) {
        pages = parsed;
      }
    }

    updatePastPaper(
      {
        pastPaperId,
        updates: { [field]: pages },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex items-center gap-2">
        <input
          {...register('pages')}
          type="text"
          placeholder="e.g. 2, 3, 4"
          className="flex-1 px-4 py-3 text-gray-900 border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={isPending}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <button
          type="submit"
          disabled={isPending}
          className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50"
          title="Save"
        >
          <CheckIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={isPending}
          className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          title="Cancel"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900 flex-1">
        {displayValue}
      </div>
      <button
        onClick={handleStartEdit}
        className="p-2 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Edit"
      >
        <PencilIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PastPaperReadingInsertEditor;
